import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import {
  effectiveViewRoles,
  hasContractCreateAccess,
  hasContractViewAccess,
  hasProjectCreateAccess,
  hasProjectViewAccess,
  hasSourcingCreateAccess,
  hasSourcingViewAccess,
  isOwnerOrAdmin,
} from "../auth/utils";
import { someDepartmentAllowsViewingContracts } from "../departments/eventCreationDepartments";
import { useViewAsRole } from "./viewAsRoleStore";

export const useSub = () => {
  const authState = useLoggedInWithOrgContextState();
  const viewAsRole = useViewAsRole();

  if (!authState) {
    throw Error("Auth state with org context not available");
  }

  const isSupplier = !authState.selectedOrg.subscription.hasBuyerAccess;
  const managesContracts =
    authState.selectedOrg.subscription.hasBuyerAccess || authState.selectedOrg.subscription.hasContractAccess;
  // An admin's "view as role" preview narrows the role-derived flags. The BFF-computed create
  // permissions can stem from department-scoped grants outside the org role list, so they are only
  // clamped by the previewed role while a preview is active — never by the real role list.
  // The preview is a v2-only concept: outside /v2 (V1 pages, the wizards) the real roles always
  // apply. window.location rather than the router keeps useSub renderable without a Router
  // provider; ViewAsRoleScope clears the state on leaving /v2, this check guarantees the very
  // first render outside v2 is already unaffected.
  const previewInScope = window.location.pathname.startsWith("/v2");
  const realRoles = authState.selectedOrg.roles;
  const roles = isSupplier || !previewInScope ? realRoles : effectiveViewRoles(realRoles, viewAsRole);
  const previewActive = roles !== realRoles;

  return {
    isOrgAdmin: isOwnerOrAdmin(roles),
    // Role-based view access. Suppliers are not role-managed per domain, so they keep
    // access to their own (supplier-facing) views regardless of org roles.
    canViewSourcingEvents: isSupplier || hasSourcingViewAccess(roles),
    canViewContracts:
      managesContracts &&
      (hasContractViewAccess(roles) ||
        (!previewActive && someDepartmentAllowsViewingContracts(authState.selectedOrg))),
    viewsContractsAsSupplier: !managesContracts,
    canViewProjects: isSupplier || hasProjectViewAccess(roles),
    canCreateProjects: authState.selectedOrg.canCreateProject && (!previewActive || hasProjectCreateAccess(roles)),
    canReportNeeds:
      authState.selectedOrg.canReportNeed &&
      (hasSourcingViewAccess(roles) || hasContractViewAccess(roles) || hasProjectViewAccess(roles)),
    canAssessNeeds:
      authState.selectedOrg.subscription.hasNeedsAccess && (isSupplier || hasSourcingViewAccess(roles)),
    canUseContractSearch:
      authState.selectedOrg.subscription.hasFencyContractSearchAccess &&
      (!managesContracts || hasContractViewAccess(roles)),
    hasContractAccess: authState.selectedOrg.subscription.hasContractAccess,
    hasBuyerAccess: authState.selectedOrg.subscription.hasBuyerAccess,
    isSupplier,
    hasSignatureAccess: authState.selectedOrg.subscription.hasSignatureAccess,
    hasBankIdAccess: authState.selectedOrg.subscription.hasBankIdAccess,
    canCreateEvents: authState.selectedOrg.canCreateEvent && (!previewActive || hasSourcingCreateAccess(roles)),
    canCreateContracts: authState.selectedOrg.canCreateContract && (!previewActive || hasContractCreateAccess(roles)),
    hasProductAccess: authState.selectedOrg.hasProductAccess,
    hasProjectAccess: authState.selectedOrg.hasProjectAccess,
    hasAiAccess: authState.selectedOrg.hasAiAccess,
    hasFencyContractSearchAccess: authState.selectedOrg.subscription.hasFencyContractSearchAccess,
    hasAssistantAccess: authState.selectedOrg.subscription.hasAssistantAccess,
    hasNeedsAccess: authState.selectedOrg.subscription.hasNeedsAccess,
    fencySyncEnabled: authState.selectedOrg.subscription.fencySyncEnabled,
    hasDocumentTemplateAccess: authState.selectedOrg.hasDocumentTemplateAccess,
    hasIFRSAccess: authState.selectedOrg.hasIFRSAccess,
    hasOrganizationAssessmentAccess: authState.selectedOrg.subscription.hasOrganizationAssessmentAccess,
    hasNegotiationAcces: authState.selectedOrg.subscription.hasNegotiationAccess,
  };
};

export type SubAccess = ReturnType<typeof useSub>;
